import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { appBadgeSupported, setAppBadgeCount } from '../../lib/app-badge';
import { useAuth } from '../../contexts/AuthContext';
import { isInternalRole } from '../../lib/roles';
import { resyncPush } from '../../lib/push';

/**
 * Three jobs, no UI:
 *
 * 1. On every sign-in and app start, re-register this device for push — but
 *    only if the person already said Allow (resyncPush never prompts). That
 *    puts right a new person on the phone, a rotated server key, or a device
 *    the server disabled after a failed send.
 * 2. Follow a tapped notification when the app is already open: the service
 *    worker posts PUSH_NAVIGATE with an in-app path and this moves the router
 *    there, so nothing open elsewhere in the app is reloaded away.
 *
 * 3. Keep the app-icon badge on the bell's unread count while the app is open:
 *    the SAME query as the bell (same key, same fetch, every 30 s and on
 *    focus), so reading or clearing notifications brings the badge down. A
 *    failed read leaves the badge alone — it never writes a guessed 0.
 *
 * Mounted once, inside the Router (App.tsx), beside SyncInit.
 */
export default function PushInit() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id;
  const internal = isInternalRole(user?.role);

  useEffect(() => {
    if (!isAuthenticated || !userId || !internal) return;
    void resyncPush();
  }, [isAuthenticated, userId, internal]);

  // Same key and fetch as NotificationBell, so both read one cache entry.
  const { data: countData, isSuccess } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.notifications.unreadCount().then(r => r.data?.data ?? r.data ?? { count: 0 }),
    refetchInterval: 30_000,
    enabled: Boolean(isAuthenticated && userId && internal && appBadgeSupported()),
  });
  const badgeCount = isSuccess && typeof countData?.count === 'number' ? countData.count : null;
  useEffect(() => {
    if (badgeCount !== null) void setAppBadgeCount(badgeCount);
  }, [badgeCount]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const url = e.data?.type === 'PUSH_NAVIGATE' ? e.data.url : null;
      // Same-origin PATHS only — the worker already strips anything else.
      if (typeof url === 'string' && url.startsWith('/')) navigate(url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  return null;
}
